import React, { useState, useEffect } from 'react'
import { message, Pagination, Modal } from 'antd'
import { getStories, deleteStory, updateStory, getUsers } from '../../../Web Apis/webApis'
import Load from '../../Load/Load'
import { AiFillDelete, AiFillEdit, AiOutlineEye } from "react-icons/ai";
import { FaSearch, FaPlus } from 'react-icons/fa';
import { BsBook } from 'react-icons/bs';
import { useNavigate } from 'react-router-dom';

const { confirm } = Modal;

const AdminStories = () => {
    const navigate = useNavigate()
    const [stories, setStories] = useState([])
    const [users, setUsers] = useState([])
    const [loading, setLoading] = useState(false)
    const [showEditStory, setShowEditStory] = useState(false)
    const [showViewStory, setShowViewStory] = useState(false)
    const [selectedStory, setSelectedStory] = useState({})
    const [action, setAction] = useState(false)
    const [currentPage, setCurrentPage] = useState(1)
    const [itemsPerPage] = useState(10)
    const [searchQuery, setSearchQuery] = useState("")

    // Form states
    const [formData, setFormData] = useState({
        story: '',
        metadata: {
            title: '',
            genre: '',
            style: '',
            learningLevel: ''
        }
    })

    const handlePageChange = (page) => {
        setCurrentPage(page)
    }

    // Calculate the current items for the page
    const indexOfLastItem = currentPage * itemsPerPage
    const indexOfFirstItem = indexOfLastItem - itemsPerPage

    const filteredStories = stories.filter((story) => {
        const query = searchQuery.toLowerCase()
        const matchesSearch = (
            story.metadata?.title?.toLowerCase().includes(query) ||
            story.story?.toLowerCase().includes(query) ||
            story.metadata?.genre?.toLowerCase().includes(query) ||
            getUserName(story.user)?.toLowerCase().includes(query)
        )

        return matchesSearch
    })

    const currentStories = filteredStories.slice(indexOfFirstItem, indexOfLastItem)

    const fetchStories = async () => {
        try {
            setLoading(true)
            const response = await getStories()
            if (response?.data?.success) {
                setStories(response?.data?.data || [])
            }
        } catch (error) {
            console.log("Error fetching stories", error.message)
            if (error.message === 'Network Error' || error.message.includes('ERR_INTERNET_DISCONNECTED')) {
                message.error("Network Error")
                return
            }
            if (error?.response?.status === 401) {
                window.alert("Your account is logged in to another device, please login again!")
                localStorage.removeItem('token')
                window.location.href = "/login"
            }
            if (error?.response?.status === 500) {
                message.error("Server not responding")
            } else {
                message.error("Error fetching stories")
            }
        } finally {
            setLoading(false)
        }
    }

    const fetchUsers = async () => {
        try {
            const response = await getUsers()
            if (response?.data?.success) {
                setUsers(response?.data?.data || [])
            }
        } catch (error) {
            console.log("Error fetching users", error.message)
        }
    }

    useEffect(() => {
        fetchStories()
        fetchUsers()
    }, [action])

    const getUserName = (userId) => {
        const user = users.find(u => u._id === userId)
        return user ? user.name : 'Unknown User'
    }

    const handleDeleteStory = async (story) => {
        confirm({
            title: 'Are you sure you want to delete this story?',
            content: `Story: ${story.metadata?.title || 'Untitled'}`,
            okText: 'Yes',
            okType: 'danger',
            cancelText: 'No',
            onOk: async () => {
                try {
                    setLoading(true)
                    const response = await deleteStory(story._id)
                    if (response?.data?.success) {
                        message.success(response?.data?.message || "Story deleted successfully")
                        setAction(!action)
                    }
                } catch (error) {
                    console.log("Error deleting story", error.message)
                    message.error(error?.response?.data?.message || "Error deleting story")
                } finally {
                    setLoading(false)
                }
            }
        })
    }

    const handleViewStory = (story) => {
        setSelectedStory(story)
        setShowViewStory(true)
    }

    const handleEditStory = (story) => {
        setSelectedStory(story)
        setFormData({
            story: story.story,
            metadata: {
                title: story.metadata?.title || '',
                genre: story.metadata?.genre || '',
                style: story.metadata?.style || '',
                learningLevel: story.metadata?.learningLevel || ''
            }
        })
        setShowEditStory(true)
    }

    const handleFormSubmit = async () => {
        try {
            setLoading(true)
            const response = await updateStory(selectedStory._id, formData)
            if (response?.data?.success) {
                message.success(response?.data?.message || "Story updated successfully")
                setAction(!action)
                setShowEditStory(false)
            }
        } catch (error) {
            console.log("Error updating story", error.message)
            message.error(error?.response?.data?.message || "Error updating story")
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className="p-6 mt-16">
            {/* Header Section with Add Button */}
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h2 className="text-3xl font-bold text-gray-800">Story Management</h2>
                    <p className="text-gray-600 mt-1">Manage AI-generated stories and user content</p>
                </div>
                <div className="flex gap-3">
                    <button
                        onClick={() => navigate('/admin/stories/add')}
                        className="bg-gradient-to-r from-green-600 to-green-700 text-white px-6 py-3 rounded-lg hover:from-green-700 hover:to-green-800 transition-all duration-200 flex items-center gap-3 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                    >
                        <FaPlus className="text-lg" />
                        <span className="font-medium">Generate New Story</span>
                    </button>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-gradient-to-r from-green-500 to-green-600 text-white p-4 rounded-lg shadow-md">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-green-100 text-sm">Total Stories</p>
                            <p className="text-2xl font-bold">{stories.length}</p>
                        </div>
                        <div className="text-green-200 text-2xl">
                            <BsBook />
                        </div>
                    </div>
                </div>
                <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-4 rounded-lg shadow-md">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-blue-100 text-sm">This Week</p>
                            <p className="text-2xl font-bold">0</p>
                        </div>
                        <div className="text-blue-200 text-2xl">
                            <AiOutlineEye />
                        </div>
                    </div>
                </div>
                <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white p-4 rounded-lg shadow-md">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-purple-100 text-sm">Fantasy Genre</p>
                            <p className="text-2xl font-bold">{stories.filter(s => s.metadata?.genre === 'fantasy').length}</p>
                        </div>
                        <div className="text-purple-200 text-2xl">
                            <BsBook />
                        </div>
                    </div>
                </div>
                <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white p-4 rounded-lg shadow-md">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-orange-100 text-sm">Advanced Level</p>
                            <p className="text-2xl font-bold">{stories.filter(s => s.metadata?.learningLevel === 'fluent').length}</p>
                        </div>
                        <div className="text-orange-200 text-2xl">
                            <AiFillEdit />
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white p-4 rounded-lg shadow-md mb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                        <div className="relative">
                            <FaSearch className="absolute left-3 top-3 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search stories..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                    </div>
                    <div className="flex items-end">
                        <button
                            onClick={() => setSearchQuery("")}
                            className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 transition duration-200 w-full"
                        >
                            Clear Search
                        </button>
                    </div>
                </div>
            </div>

            {/* Stories Table */}
            <div className="bg-white shadow-md rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">S.No</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Author</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Genre</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Level</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Word Count</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {currentStories?.length > 0 ? (
                                currentStories?.map((story, index) => (
                                    <tr key={story._id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {(currentPage - 1) * itemsPerPage + index + 1}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-medium text-gray-900 max-w-xs truncate" title={story.metadata?.title}>
                                                {story.metadata?.title || 'Untitled'}
                                            </div>
                                            <div className="text-sm text-gray-500 max-w-xs truncate" title={story.story}>
                                                {story.story?.substring(0, 50)}...
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {getUserName(story.user)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                                                {story.metadata?.genre || 'N/A'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                                story.metadata?.learningLevel === 'starter' ? 'bg-green-100 text-green-800' :
                                                story.metadata?.learningLevel === 'beginner' ? 'bg-yellow-100 text-yellow-800' :
                                                story.metadata?.learningLevel === 'intermediate' ? 'bg-orange-100 text-orange-800' :
                                                'bg-red-100 text-red-800'
                                            }`}>
                                                {story.metadata?.learningLevel || 'N/A'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {story.metadata?.wordCount || 0}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {new Date(story.createdAt).toLocaleDateString()}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                            <div className="flex items-center space-x-2">
                                                <button
                                                    onClick={() => handleViewStory(story)}
                                                    className="text-blue-600 hover:text-blue-900 p-1"
                                                    title="View"
                                                >
                                                    <AiOutlineEye className="text-lg" />
                                                </button>
                                                <button
                                                    onClick={() => handleEditStory(story)}
                                                    className="text-green-600 hover:text-green-900 p-1"
                                                    title="Edit"
                                                >
                                                    <AiFillEdit className="text-lg" />
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteStory(story)}
                                                    className="text-red-600 hover:text-red-900 p-1"
                                                    title="Delete"
                                                >
                                                    <AiFillDelete className="text-lg" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                                        {loading ? "Loading stories..." : "No stories found"}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination */}
            {filteredStories.length > itemsPerPage && (
                <div className="flex justify-center mt-6">
                    <Pagination
                        current={currentPage}
                        total={filteredStories.length}
                        pageSize={itemsPerPage}
                        onChange={handlePageChange}
                        showSizeChanger={false}
                        showQuickJumper
                        showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} stories`}
                    />
                </div>
            )}

            {/* Edit Story Modal */}
            {showEditStory && (
                <Modal
                    title="Edit Story"
                    open={showEditStory}
                    onCancel={() => setShowEditStory(false)}
                    footer={[
                        <button
                            key="cancel"
                            onClick={() => setShowEditStory(false)}
                            className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 mr-2"
                        >
                            Cancel
                        </button>,
                        <button
                            key="submit"
                            onClick={handleFormSubmit}
                            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                        >
                            Update
                        </button>
                    ]}
                    width={800}
                >
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                            <input
                                type="text"
                                value={formData.metadata.title}
                                onChange={(e) => setFormData({
                                    ...formData,
                                    metadata: {...formData.metadata, title: e.target.value}
                                })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Enter story title"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Genre</label>
                                <input
                                    type="text"
                                    value={formData.metadata.genre}
                                    onChange={(e) => setFormData({
                                        ...formData,
                                        metadata: {...formData.metadata, genre: e.target.value}
                                    })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="e.g., fantasy, sci-fi"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Style</label>
                                <input
                                    type="text"
                                    value={formData.metadata.style}
                                    onChange={(e) => setFormData({
                                        ...formData,
                                        metadata: {...formData.metadata, style: e.target.value}
                                    })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="e.g., narrative, descriptive"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Learning Level</label>
                            <select
                                value={formData.metadata.learningLevel}
                                onChange={(e) => setFormData({
                                    ...formData,
                                    metadata: {...formData.metadata, learningLevel: e.target.value}
                                })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="">Select level</option>
                                <option value="starter">Starter</option>
                                <option value="beginner">Beginner</option>
                                <option value="intermediate">Intermediate</option>
                                <option value="fluent">Fluent</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Story Content</label>
                            <textarea
                                value={formData.story}
                                onChange={(e) => setFormData({...formData, story: e.target.value})}
                                rows={15}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Enter story content"
                            />
                        </div>
                    </div>
                </Modal>
            )}

            {/* View Story Modal */}
            {showViewStory && (
                <Modal
                    title="View Story"
                    open={showViewStory}
                    onCancel={() => setShowViewStory(false)}
                    footer={[
                        <button
                            key="close"
                            onClick={() => setShowViewStory(false)}
                            className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600"
                        >
                            Close
                        </button>
                    ]}
                    width={1000}
                >
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <h3 className="text-lg font-medium text-gray-900">{selectedStory.metadata?.title || 'Untitled'}</h3>
                                <p className="text-sm text-gray-500">Author: {getUserName(selectedStory.user)}</p>
                            </div>
                            <div className="text-right">
                                <p className="text-sm text-gray-500">Genre: {selectedStory.metadata?.genre || 'N/A'}</p>
                                <p className="text-sm text-gray-500">Level: {selectedStory.metadata?.learningLevel || 'N/A'}</p>
                                <p className="text-sm text-gray-500">Words: {selectedStory.metadata?.wordCount || 0}</p>
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Story Content</label>
                            <div className="border border-gray-300 rounded-md p-4 max-h-96 overflow-y-auto bg-gray-50 whitespace-pre-wrap">
                                {selectedStory.story}
                            </div>
                        </div>
                    </div>
                </Modal>
            )}

            {loading && <Load />}
        </div>
    )
}

export default AdminStories
